'use client';

import React from 'react';
import {
    Grid,
    GridItem,
    Heading,
    Button,
    Icons,
    Text,
    HStack,
    Box,
    Badge,
    Flex,
} from '@shared/ui';
import { Picture } from 'app/components/Picture';
import i18n from '@shared/i18n';
import { type CartProductCardType } from './types';
import { ProductCounter } from './ProductCounter';

type CartProductCardProps = Omit<CartProductCardType, 'giveaways' | 'hasGiveaway'> & {
    onChange: (id: string, quantity: number) => void;
    onDelete?: (id: string) => void;
    isAddonProduct?: boolean;
    showQuantityOnly?: boolean;
    defaultSrc?: string;
};

const CartProductCardGridTemplateAreas = `
    'product-image product-name cancel'
    'product-image product-spec cancel'
    '. product-bottom cancel'
`;

const CartProductCardGridAddonTemplateAreas = `
    'product-image product-name cancel'
    'product-image product-bottom cancel'
`;

export const ProductCard: React.FC<CartProductCardProps> = (props) => {
    const {
        id,
        name,
        badge,
        price,
        discountedPrice,
        alertInfo,
        quantity,
        specInfo,
        message,
        image,
        status,
        onChange,
        onDelete,
        maxLimit,
        isAddonProduct = false,
        showQuantityOnly = false,
        defaultSrc,
    } = props;

    const enable = status === 'active';
    const imageSize = isAddonProduct ? '65px' : '56px';

    return (
        <Box>
            <Grid
                sx={{
                    '& > *:not([data-area="cancel"])': {
                        opacity: enable ? 1 : 0.3,
                        pointerEvents: enable ? 'auto' : 'none',
                    },
                }}
                id={id}
                gridTemplateAreas={
                    isAddonProduct && !specInfo
                        ? CartProductCardGridAddonTemplateAreas
                        : CartProductCardGridTemplateAreas
                }
                gridTemplateColumns="auto 1fr auto"
                gap="xs"
                py="xxs"
                overflow="hidden"
            >
                <GridItem area="product-image">
                    <Box
                        w={imageSize}
                        h={imageSize}
                        borderRadius="base"
                        overflow="hidden"
                        position="relative"
                    >
                        <Picture
                            defaultSrc={defaultSrc}
                            src={image.src}
                            alt={name}
                            width={100}
                            height={100}
                        />
                        {badge && (
                            <Badge
                                position="absolute"
                                top="2px"
                                left="2px"
                                bg="secondary.040"
                                color="neutral.000"
                                fontSize="base"
                                fontWeight="bold"
                            >
                                {badge}
                            </Badge>
                        )}
                    </Box>
                </GridItem>
                <GridItem area="product-name">
                    <Heading as="h3" fontSize="sm" fontWeight="bold" lineHeight="1.4">
                        {name}
                    </Heading>
                </GridItem>
                {specInfo && (
                    <GridItem
                        area="product-spec"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Text as="span" fontSize="base" color="neutral.070">
                            {specInfo?.split('/').join(' / ') || ''}
                        </Text>
                    </GridItem>
                )}
                <GridItem area="product-bottom">
                    <Grid gridTemplateAreas="'quantity price'">
                        <GridItem area="quantity">
                            {showQuantityOnly ? (
                                <Text fontWeight="bold" color="neutral.120">
                                    x {quantity}
                                </Text>
                            ) : (
                                <Flex alignItems="center" gap="xxs">
                                    <ProductCounter
                                        key={id + quantity}
                                        id={id}
                                        quantity={quantity}
                                        onChange={onChange}
                                        max={maxLimit}
                                    />
                                    {maxLimit && quantity >= maxLimit && (
                                        <Text as="span" color="secondary.060">
                                            {i18n.gettext('已達數量上限')}
                                        </Text>
                                    )}
                                </Flex>
                            )}
                        </GridItem>
                        <GridItem area="price" alignContent="center">
                            <HStack
                                justifySelf="end"
                                gap="xs"
                                color="neutral.120"
                                fontWeight="bold"
                                fontSize="sm"
                                flexDirection="row-reverse"
                                alignItems="flex-end"
                                mt={isAddonProduct ? '3xl' : '0'}
                            >
                                {discountedPrice && (
                                    <Text
                                        as="strong"
                                        sx={{
                                            '& ~ strong': {
                                                textDecoration: 'line-through',
                                                color: 'neutral.060',
                                                fontSize: 'base',
                                                mb: 'least',
                                            },
                                        }}
                                    >
                                        {discountedPrice.value}
                                    </Text>
                                )}
                                {price && <Text as="strong">{price.value}</Text>}
                            </HStack>
                        </GridItem>
                    </Grid>
                </GridItem>
                <GridItem area="cancel" data-area="cancel">
                    {onDelete && (
                        <Button
                            position="relative"
                            variant="plain"
                            color="neutral.070"
                            p="0 !important"
                            _hover={{
                                color: 'neutral.120',
                            }}
                            // 確保點擊區域夠大
                            _after={{
                                position: 'absolute',
                                content: '""',
                                display: 'block',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '44px',
                                height: '44px',
                            }}
                            onClick={() => onDelete(id)}
                        >
                            <Icons.csr.X size="16px" weight="bold" />
                        </Button>
                    )}
                </GridItem>
            </Grid>
            {alertInfo && (
                <Text
                    display="block"
                    as="strong"
                    color="negative.050"
                    fontSize="base"
                    textAlign="right"
                    mt="xs"
                >
                    {alertInfo}
                </Text>
            )}
            {message && (
                <Text
                    display="block"
                    as="strong"
                    color="negative.050"
                    fontSize="base"
                    textAlign="right"
                    mt="xs"
                >
                    {message}
                </Text>
            )}
        </Box>
    );
};
